/**
 * v-tooltip — Vue directive, drop-in replacement for the native
 * `title="..."` browser tooltip.
 *
 * Same visual + behavioural contract as the UTooltip component
 * (resources/js/Components/UI/UTooltip.vue): teleports the bubble to
 * <body> so it escapes any clipping/transform ancestor, clamps to the
 * viewport edges, auto-flips above/below for keyboard accessibility
 * (mouseenter + focusin trigger). Use the directive for simple string
 * tooltips, the UTooltip component when you need rich slot content.
 *
 * Usage:
 *   v-tooltip="'Foo'"                  literal string
 *   v-tooltip="someComputedString"     reactive binding
 *   v-tooltip:bottom="'…'"             arg picks placement ('top' | 'bottom')
 *
 * Why a directive AND a component:
 *   The component is the right fit when you control the markup
 *   structure and want a slot for rich content. The directive avoids
 *   wrapping every icon-button / badge across the codebase in an extra
 *   element — important for tight flex/grid rows where an extra
 *   wrapper would disturb alignment.
 *
 * The native `title` attribute is explicitly forbidden in this
 * codebase (see MEMORY → "Never use the native `title` tooltip").
 * If you find a stray `title=` on an HTML element, replace it with
 * v-tooltip.
 */

const STATE = Symbol('v-tooltip-state');

// Module-singleton bubble. We keep ONE DOM node and reuse it across
// triggers — tooltips never stack (a new hover hides the previous
// one), so a single node is enough and avoids the unmount/remount
// churn that would happen if each directive instance owned its own.
let bubble = null;
let activeEl = null;     // the trigger currently showing the tooltip
let showTimer = null;

function ensureBubble() {
    if (bubble) return bubble;
    bubble = document.createElement('div');
    // Class mirrors the UTooltip component so design tokens stay in
    // one place. Pointer-events: none means the bubble itself never
    // steals hover from the trigger underneath.
    // z-[250] keeps the bubble above every other floating layer
    // (UAnchoredMenu = 150, SavedViewsMenu = 200) so a tooltip on
    // an item inside a dropdown still renders on top.
    // Multi-line capable: whitespace-pre-line respects `\n` from the
    // directive value as real line breaks AND still wraps long lines
    // at the max-width. max-w-sm + leading-snug + break-words keeps
    // long URLs / refs from blowing out horizontally.
    bubble.className = 'pointer-events-none fixed z-[250] max-w-sm whitespace-pre-line break-words leading-snug rounded-md bg-ink text-bg text-xs font-medium px-2 py-1 shadow-lg';
    bubble.setAttribute('role', 'tooltip');
    bubble.style.display = 'none';
    document.body.appendChild(bubble);
    return bubble;
}

function position(el, placement) {
    const b = ensureBubble();
    const rRect = el.getBoundingClientRect();
    const tRect = b.getBoundingClientRect();
    const margin = 8;

    const wantTop = placement === 'top';
    const roomAbove = rRect.top;
    const roomBelow = window.innerHeight - rRect.bottom;
    const fitsAbove = tRect.height + margin <= roomAbove;
    const fitsBelow = tRect.height + margin <= roomBelow;
    const place = wantTop
        ? (fitsAbove ? 'top' : (fitsBelow ? 'bottom' : 'top'))
        : (fitsBelow ? 'bottom' : (fitsAbove ? 'top' : 'bottom'));

    let left = rRect.left + (rRect.width - tRect.width) / 2;
    if (left < margin) left = margin;
    const right = left + tRect.width;
    if (right > window.innerWidth - margin) {
        left -= right - (window.innerWidth - margin);
    }
    const top = place === 'top'
        ? rRect.top - tRect.height - 6
        : rRect.bottom + 6;
    b.style.left = left + 'px';
    b.style.top  = top  + 'px';
}

function hide() {
    clearTimeout(showTimer);
    showTimer = null;
    if (bubble) bubble.style.display = 'none';
    activeEl = null;
    window.removeEventListener('scroll', onScrollHide, true);
}

function onScrollHide() { hide(); }

function show(el, state) {
    // Last line of defence: strip any native title right before we show
    // our bubble. Covers titles added by non-Vue means (a third-party
    // script, a late attribute set) that the mount/update strips missed.
    stripNativeTitle(el, state);
    const text = state.text;
    if (!text) return;
    const b = ensureBubble();
    b.textContent = text;
    b.style.display = 'block';
    activeEl = el;
    // Tooltip must hide on any scroll while open — keeps it from
    // floating away from a scrolled-off trigger.
    window.addEventListener('scroll', onScrollHide, true);
    // Measure after the DOM has applied display:block.
    requestAnimationFrame(() => {
        if (activeEl === el) position(el, state.placement);
    });
}

function scheduleShow(el, state) {
    clearTimeout(showTimer);
    showTimer = setTimeout(() => show(el, state), state.delay);
}

// The directive's bubble owns the tooltip slot, so any stray native
// `title` on the element must go — otherwise the OS renders its own
// bubble alongside ours (the recurring "double tooltip" bug). Critically
// we run this on mount AND on every update: a reactive `:title` binding
// is re-applied by Vue on each patch, so a one-time mount strip lets the
// title silently reappear. If v-tooltip itself was given no text, a
// pre-existing title is carried into the directive's state so its
// content isn't lost.
function stripNativeTitle(el, state) {
    if (!el || typeof el.hasAttribute !== 'function' || !el.hasAttribute('title')) return;
    const carried = el.getAttribute('title') || '';
    if (!state.text && carried) state.text = carried;
    el.removeAttribute('title');
}

function attach(el, state) {
    el[STATE] = state;
    state.onEnter = () => scheduleShow(el, state);
    state.onLeave = () => hide();
    // focusin/focusout for keyboard accessibility — Tab into a button
    // surfaces the tooltip too. Capture-phase isn't needed; the
    // events bubble.
    el.addEventListener('mouseenter', state.onEnter);
    el.addEventListener('mouseleave', state.onLeave);
    el.addEventListener('focusin',    state.onEnter);
    el.addEventListener('focusout',   state.onLeave);
    stripNativeTitle(el, state);
}

function detach(el) {
    const state = el[STATE];
    if (!state) return;
    el.removeEventListener('mouseenter', state.onEnter);
    el.removeEventListener('mouseleave', state.onLeave);
    el.removeEventListener('focusin',    state.onEnter);
    el.removeEventListener('focusout',   state.onLeave);
    if (activeEl === el) hide();
    delete el[STATE];
}

function resolveText(value) {
    if (value === null || value === undefined || value === false) return '';
    return String(value);
}

export default {
    // Vue 3 directive. Argument (v-tooltip:bottom) picks placement;
    // value is the text. Defaults: placement = 'top', delay = 80 ms
    // to match the UTooltip component.
    mounted(el, binding) {
        const state = {
            text:      resolveText(binding.value),
            placement: binding.arg === 'bottom' ? 'bottom' : 'top',
            delay:     80,
        };
        attach(el, state);
    },
    updated(el, binding) {
        const state = el[STATE];
        if (!state) return;
        state.text = resolveText(binding.value);
        state.placement = binding.arg === 'bottom' ? 'bottom' : 'top';
        // Re-strip: Vue may have just re-applied a reactive `:title`
        // during this patch. Without this the native bubble returns
        // after the first update → the double-tooltip the user sees.
        stripNativeTitle(el, state);
        // If we're currently showing this tooltip and the text changed,
        // re-render the bubble in place.
        if (activeEl === el && bubble) {
            bubble.textContent = state.text;
            if (!state.text) hide();
            else position(el, state.placement);
        }
    },
    unmounted(el) {
        detach(el);
    },
};
